<template>
  <div class="container-wrapper my-4 pt-sm-3 pb-5">
    <h4 class="text-center mb-4 pb-2">{{ vehicleName || selectedModelName }}</h4>
    <div class="row">
      <div v-for="(item, index) in trims" :key="`card-${index}`" class="col-sm-6">
        <div class="trim-card" :class="{ flipped: flippedCards[`card${index}`] }">
          <div class="trim-card-content">
            <div class="trim-card-front">
              <div class="trim-card-header text-center px-3 pt-4">
                <div class="trim-name">
                  <div class="font-weight-bold">{{ `${item.vehicle.trim} ${item.vehicle.style}` }}</div>
                  <div class="transmission text-muted">
                    {{ $t(`message.buildAndPrice.transmission.${item.vehicle.transmission}`) }}
                  </div>
                </div>
                <img
                  class="px-4 px-sm-3 mt-sm-5"
                  :alt="`${item.vehicle.trim} ${item.vehicle.style}`"
                  :src="item.vehicle.image || require('@/assets/vehicle-fallback.png')"
                />
              </div>
              <div class="trim-card-footer p-3">
                <div class="text-muted">{{ $t('message.buildAndPrice.trimSelect.startingAt') }}</div>
                <div class="h4 mt-0 mb-3">
                  {{ $n(item.vehicle.vehiclePrice.base.msrp, 'currencyDollars', languageCode) }}
                </div>
                <mobialis-button
                  @click="selectTrim(item.vehicle.id)"
                  :class="{ active: selectedTrimId === item.vehicle.id }"
                  :aria-pressed="selectedTrimId === item.vehicle.id"
                  size="btn-md"
                  variant="secondary"
                >
                  {{
                    selectedTrimId === item.vehicle.id
                      ? $t('message.buildAndPrice.trimSelect.selected')
                      : $t('message.buildAndPrice.trimSelect.select')
                  }}
                  <span class="checkmark-icon primary-fill-svg">
                    <svg xmlns="http://www.w3.org/2000/svg" class="info" width="20" height="20" viewBox="0 0 20 20">
                      <path
                        fill="#007bc7"
                        fill-rule="evenodd"
                        d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-8.925 4.236L15.01 8.3a.516.516 0 0 0 0-.73l-.73-.73a.516.516 0 0 0-.73 0l-4.84 4.841-2.26-2.26a.516.516 0 0 0-.73 0l-.73.73a.516.516 0 0 0 0 .73l3.355 3.355a.516.516 0 0 0 .73 0z"
                      ></path>
                    </svg>
                  </span>
                </mobialis-button>
                <mobialis-button @click="viewTrimDetails(index, item.vehicle.id)" size="btn-md" variant="link">{{
                  $t('message.buildAndPrice.trimSelect.keyFeatures')
                }}</mobialis-button>
              </div>
            </div>
            <div class="trim-card-back pt-4 p-3">
              <div class="text-center trim-name">
                <div class="font-weight-bold">{{ `${item.vehicle.trim} ${item.vehicle.style}` }}</div>
                <div class="transmission text-muted">
                  {{ $t(`message.buildAndPrice.transmission.${item.vehicle.transmission}`) }}
                </div>
              </div>
              <div class="key-features">
                <div v-for="keyFeature in keyFeatures" :key="keyFeature">
                  <template v-if="item.vehicle.keyFeatures[keyFeature]">
                    <div class="mb-2 key-features-heading">
                      {{ $t(`message.buildAndPrice.keyFeatures.${keyFeature}`) }}
                    </div>
                    <ul>
                      <li v-for="(item, index) in item.vehicle.keyFeatures[keyFeature]" :key="index">{{ item }}</li>
                    </ul>
                  </template>
                </div>
              </div>
              <div class="trim-card-footer p-3">
                <mobialis-button
                  @click="selectTrim(item.vehicle.id)"
                  :class="{ active: selectedTrimId === item.vehicle.id }"
                  :aria-pressed="selectedTrimId === item.vehicle.id"
                  size="btn-md"
                  variant="secondary"
                >
                  {{
                    selectedTrimId === item.vehicle.id
                      ? $t('message.buildAndPrice.trimSelect.selected')
                      : $t('message.buildAndPrice.trimSelect.select')
                  }}
                  <span class="checkmark-icon primary-fill-svg">
                    <svg xmlns="http://www.w3.org/2000/svg" class="info" width="20" height="20" viewBox="0 0 20 20">
                      <path
                        fill="#007bc7"
                        fill-rule="evenodd"
                        d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-8.925 4.236L15.01 8.3a.516.516 0 0 0 0-.73l-.73-.73a.516.516 0 0 0-.73 0l-4.84 4.841-2.26-2.26a.516.516 0 0 0-.73 0l-.73.73a.516.516 0 0 0 0 .73l3.355 3.355a.516.516 0 0 0 .73 0z"
                      ></path>
                    </svg>
                  </span>
                </mobialis-button>
                <mobialis-button @click="viewTrimDetails(index, item.vehicle.id)" size="btn-md" variant="link">{{
                  $t('message.buildAndPrice.trimSelect.back')
                }}</mobialis-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'TrimSelect',
  data() {
    return {
      flippedCards: {},
      keyFeatures: ['wheels', 'entertainment', 'interiorSafety'],
    };
  },
  computed: {
    ...mapState('buildAndPrice', ['selectedTrimId', 'trims']),
    ...mapGetters({
      languageCode: 'user/getLanguageCodeForNumericalInternationalization',
      selectedModelName: 'buildAndPrice/selectedModelName',
      vehicleName: 'buildAndPrice/vehicleName',
    }),
  },
  methods: {
    viewTrimDetails(index, id) {
      if (window.innerWidth < this.enums.DeviceWidth.SM) {
        this.$store.commit('buildAndPrice/SET_STATE', ['viewTrimDetailsId', id]);
        this.$router.push('trim-details');
      } else {
        this.$set(this.flippedCards, `card${index}`, !this.flippedCards[`card${index}`]);
      }
    },
    async selectTrim(value) {
      try {
        if (value !== this.selectedTrimId) {
          this.$store.commit('SET_LOADING', true);
          this.$store.commit('buildAndPrice/RESET_TRIM');
          this.$store.commit('buildAndPrice/RESET_SELECTED_OPTIONS');
          this.$store.commit('buildAndPrice/SET_STATE', ['selectedTrimId', value]);
          await this.$store.dispatch('buildAndPrice/getBuildAndPriceConfig', value);
        }
        this.$router.push('colours-select');
      } catch (e) {
        this.$router.push('error');
        this.$store.commit('SET_LOADING', false);
        this.$log('Error getting build and price configuration', e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/variables';

img {
  max-width: 100%;
}

.trim-card {
  width: 100%;
  margin-bottom: 30px;
}

.trim-card-content {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.trim-card-back {
  display: none;
}

.trim-card-header {
  background: #f0f4f7;
}

.transmission {
  font-size: 12px;
  line-height: 18px;
}

.key-features {
  max-height: 250px;
  overflow: auto;
  margin-top: 70px;
  margin-bottom: 20px;
  padding-right: 10px;
  font-size: 12px;
  line-height: 18px;
  .key-features-heading {
    font-weight: 700;
    font-size: 14px;
    line-height: 18px;
  }
  ul {
    padding-left: 20px;
    margin-bottom: 10px;
    margin-top: 5px;
    li {
      margin-bottom: 5px;
    }
  }
}

// -- card flip styling for desktop only

@media (min-width: 575px) {
  .trim-name {
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    padding: 1.5rem 1rem;
  }
  .trim-card-footer {
    position: absolute;
    width: 100%;
    left: 0;
    bottom: 0;
  }
  .trim-card {
    height: 460px;
    perspective: 1200px;
  }
  .trim-card-content {
    position: absolute;
    width: 100%;
    height: 100%;

    transition: transform 1s;
    transform-style: preserve-3d;
  }

  .trim-card.flipped {
    .trim-card-back {
      pointer-events: inherit;
    }
    .trim-card-content {
      transform: rotateY(180deg);
      transition: transform 0.5s;
    }
  }

  .trim-card-front,
  .trim-card-back {
    display: block;
    position: absolute;
    height: 100%;
    width: 100%;
    background: white;
    backface-visibility: hidden;
  }

  .trim-card-back {
    background: white;
    transform: rotateY(180deg);
    pointer-events: none;
  }
}

// -- selected button styling

button.btn-secondary::v-deep {
  .checkmark-icon {
    display: none;
  }
}

button.btn-secondary:not(:disabled):not(.disabled).active::v-deep {
  background: $primary-light-background;
  border: 1px solid $primary;
  color: #19222b;
  position: relative;
  .checkmark-icon {
    display: block;
    position: absolute;
    right: 10px;
    top: 5px;
  }
}
</style>
